/** Typed API for the operator console: auth + admin endpoints. */
import { apiRequest } from '@/lib/api'
import type {
  AdminAccountDto,
  AdminFxConversionDto,
  AdminRevenueFigureDto,
  AdminReviewDto,
  AdminRiskAssessmentDto,
  AdminTransactionDto,
  AdminUser,
  AuditLogDto,
  AuthUser,
  KycPendingDto,
  LoginResponse,
  Page,
  SecurityEventDto,
  WebhookDto,
} from '@/lib/types'

export interface RevenueUpsertBody {
  period: string
  currency: string
  subscriptions_cents: number
  interchange_cents: number
  fx_margin_cents: number
  paylink_cents: number
  notes?: string
}

export const authApi = {
  login: (email: string, password: string, twoFactorCode?: string) =>
    apiRequest<LoginResponse>('/auth/login', {
      method: 'POST',
      auth: false,
      body: { email, password, ...(twoFactorCode ? { two_factor_code: twoFactorCode } : {}) },
    }),
  me: () => apiRequest<{ user: AuthUser }>('/auth/me'),
  logout: (refreshToken?: string) =>
    apiRequest<void>('/auth/logout', {
      method: 'POST',
      body: refreshToken ? { refresh_token: refreshToken } : {},
    }),
}

interface UserListParams {
  status?: string
  search?: string
  page?: number
}

function qs(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
  if (entries.length === 0) return ''
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&')
}

export const adminApi = {
  listUsers: (params: UserListParams) =>
    apiRequest<Page<AdminUser>>(`/admin/users${qs({ ...params })}`),
  getUser: (id: string) =>
    apiRequest<{ user: AdminUser; audit_logs: AuditLogDto[] }>(`/admin/users/${id}`),
  suspend: (id: string, reason: string) =>
    apiRequest<{ user: AdminUser }>(`/admin/users/${id}/suspend`, {
      method: 'POST',
      body: { reason },
    }),
  reactivate: (id: string) =>
    apiRequest<{ user: AdminUser }>(`/admin/users/${id}/reactivate`, { method: 'POST' }),
  forceLogout: (id: string) =>
    apiRequest<void>(`/admin/users/${id}/force-logout`, { method: 'POST' }),
  listKyc: (page = 1) => apiRequest<Page<KycPendingDto>>(`/admin/kyc${qs({ page })}`),
  listAuditLogs: (params: { action?: string; resourceType?: string; page?: number }) =>
    apiRequest<Page<AuditLogDto>>(`/admin/audit-logs${qs({ ...params })}`),
  listWebhooks: (page = 1) => apiRequest<Page<WebhookDto>>(`/admin/webhooks${qs({ page })}`),

  // Banking (Slice D)
  listAccounts: (params: { status?: string; page?: number }) =>
    apiRequest<Page<AdminAccountDto>>(`/admin/accounts${qs({ ...params })}`),
  getAccount: (id: string) =>
    apiRequest<{ account: AdminAccountDto; reviews: AdminReviewDto[] }>(`/admin/accounts/${id}`),
  listTransactions: (params: { search?: string; status?: string; page?: number }) =>
    apiRequest<Page<AdminTransactionDto>>(`/admin/transactions${qs({ ...params })}`),
  listReviews: (page = 1) => apiRequest<Page<AdminReviewDto>>(`/admin/reviews${qs({ page })}`),
  listSecurity: (params: { severity?: string; page?: number }) =>
    apiRequest<Page<SecurityEventDto>>(`/admin/security${qs({ ...params })}`),
  resolveReview: (id: string) =>
    apiRequest<void>(`/admin/reviews/${id}/resolve`, { method: 'POST' }),
  assignReview: (id: string) =>
    apiRequest<void>(`/admin/reviews/${id}/assign`, { method: 'POST', body: {} }),

  // Risk assessments (USP #2) — escalated queue + operator decision.
  listRiskAssessments: (page = 1) =>
    apiRequest<Page<AdminRiskAssessmentDto>>(`/admin/risk-assessments${qs({ page })}`),
  resolveRiskAssessment: (id: string, decision: 'approved' | 'rejected', reason: string) =>
    apiRequest<void>(`/admin/risk-assessments/${id}/resolve`, {
      method: 'POST',
      body: { decision, reason },
    }),

  // FX conversions (USP #3) — global history.
  listFxConversions: (params: { status?: string; page?: number }) =>
    apiRequest<Page<AdminFxConversionDto>>(`/admin/fx-conversions${qs({ ...params })}`),

  // Business model revenue figures (USP #7) — operator CRUD + publish.
  listRevenue: () => apiRequest<{ items: AdminRevenueFigureDto[] }>('/admin/business-model'),
  upsertRevenue: (body: RevenueUpsertBody) =>
    apiRequest<AdminRevenueFigureDto>('/admin/business-model', { method: 'POST', body }),
  publishRevenue: (id: string, published: boolean) =>
    apiRequest<AdminRevenueFigureDto>(`/admin/business-model/${id}/publish`, {
      method: 'POST',
      body: { published },
    }),
}
